import React, { ReactElement, useEffect, useState } from 'react';
import { HeadTag } from 'shared/components';
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Menu,
  MenuItem,
  Stack,
  SvgIcon,
  Typography,
} from '@mui/material';
import {
  AppFormikButtonTiles,
  AppPrimaryButton,
  ConfirmDialog,
} from 'shared/components/AppComponents';
import { Formik, useFormikContext } from 'formik';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { MCPServersService } from 'services/MCPServersService';
import { Toast, Utils } from 'shared/helpers';
import { ReactComponent as Edit } from 'assets/images/ic_edit.svg';
import { ReactComponent as Trash } from 'assets/images/ic_trash.svg';
import { ReactComponent as Back } from 'assets/images/ic_back.svg';
import BasicIdentification from './components/BasicIdentification';
import MCPSpecificDetails from './components/MCPSpecificDetails';
import CapabilityProfile from './components/CapabilityProfile';
import IdentityAndAccess from './components/IdentityAndAccess';
import { useSelector } from 'react-redux';
import { BaseApplicationState } from 'state/baseapplication/Reducer';

export const getStatusColors = (status: string): any => {
  switch (status) {
    case 'Under Review':
      return {
        background: '#083CB5',
        color: '#e7f2fd',
      };
    case 'Draft':
      return {
        background: '#828282',
        color: '#f9fafb',
      };
    case 'Approved':
      return {
        background: '#28a95e',
        color: '#e4f1e5',
      };
    case 'Rejected':
      return {
        background: '#c20000',
        color: '#ffebeb',
      };
    default:
      return {
        background: '#828282',
        color: '#f9fafb',
      };
  }
};

const ViewFormikContext = (): any => {
  const { values, setValues } = useFormikContext<any>();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state) {
      setValues({
        ...values,
        status: [false, false, true],
      });
      navigate(location.pathname, { replace: true, state: null });
    }
  }, []);

  return null;
};

const RecordMCPServer = (): ReactElement => {
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const { mcpId } = useParams();
  const [serverDetail, setServerDetail] = useState<any>([]);
  const [statusMenus] = useState<string[]>([
    'Draft',
    'Under Review',
    'Approved',
    'Rejected',
  ]);
  const [openDeleteServer, setOpenDeleteServer] = React.useState(false);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const { orgACLConfig }: BaseApplicationState = useSelector(
    (state: any) => state.baseAppState
  );
  const handleClick = (event: any): void => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const loadDetails = (mcpId: any): void => {
    setIsLoadingDetail(true);
    MCPServersService.getDetailsServer(mcpId)
      .then((response) => {
        const { data } = response.data;
        setServerDetail(data);
        setIsLoadingDetail(false);
      })
      .catch((err) => Utils.handleErrorResponse(err));
  };

  const handleUpdateApproveStatus = (data: any): any => {
    console.log(data);
    MCPServersService.updateMCPServerApproveStatus(mcpId, {
      approval_status: data,
    })
      .then((response) => {
        handleClose();
        Utils.handleSuccessResponse(response);
        loadDetails(mcpId);
      })
      .catch((err) => Utils.handleErrorResponse(err));
  };

  const handleCloseDeleteAISystem = (): void => {
    setOpenDeleteServer(false);
  };

  const handleDeleteAISystem = (id: any): void => {
    MCPServersService.deleteMCPServer(id)
      .then(() => {
        Toast.showSuccessMessage(`Successfully delete server`);
        setOpenDeleteServer(false);
        navigate(-1);
      })
      .catch((e) => {
        Toast.showErrorMessage(e);
      });
  };

  useEffect(() => {
    if (mcpId) {
      loadDetails(mcpId);
    }
  }, [mcpId]);

  return (
    <Box className="ai-inventory ai-solutions">
      <HeadTag title="AI Systems" />
      {isLoadingDetail && (
        <Box className="empty-content">
          <CircularProgress />
        </Box>
      )}
      {!isLoadingDetail && serverDetail && (
        <Stack direction="column" spacing={2}>
          <Stack direction="row">
            <Box
              sx={{
                padding: { xs: '16px 0', md: '35px 0' },
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'space-between' },
                justifyContent: 'space-between',
                gap: { xs: 2, sm: 0 },
                width: '100%',
              }}
            >
              <Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '24px !important',
                    fontWeight: '700 !important',
                  }}
                >
                  {serverDetail?.server_name}
                </Typography>
                <Typography
                  component="span"
                  variant="body1"
                  sx={{
                    fontSize: '14px !important',
                    color: '#7E7E7E',
                  }}
                >
                  MCP-{serverDetail?._id?.slice(-8) ?? ''}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  gap: '8px',
                  flexWrap: 'wrap',
                  width: { xs: '100%', sm: 'auto' },
                  justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                }}
              >
                {orgACLConfig.aiInventory?.includes(
                  'aiInventory.mcpServers.view.edit'
                ) && (
                  <AppPrimaryButton
                    className="btn-small"
                    title="Edit"
                    startIcon={
                      <SvgIcon
                        component={Edit}
                        inheritViewBox
                        className="view-icon"
                        fontSize="inherit"
                        htmlColor="white"
                      />
                    }
                    onClick={() =>
                      navigate(`/mcp-servers/edit/${serverDetail?._id}`, {
                        state: serverDetail,
                      })
                    }
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                  />
                )}
                {orgACLConfig.aiInventory?.includes(
                  'aiInventory.mcpServers.view.delete'
                ) && (
                  <AppPrimaryButton
                    title="Delete"
                    className="btn-danger btn-small"
                    startIcon={
                      <SvgIcon
                        component={Trash}
                        inheritViewBox
                        className="view-icon"
                        fontSize="inherit"
                        htmlColor="white"
                      />
                    }
                    onClick={() => {
                      setOpenDeleteServer(true);
                    }}
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                  />
                )}
                <AppPrimaryButton
                  className="btn-small"
                  title="Back"
                  startIcon={
                    <SvgIcon
                      component={Back}
                      inheritViewBox
                      className="view-icon"
                      fontSize="inherit"
                      htmlColor="white"
                    />
                  }
                  onClick={() => navigate(-1)}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                />
              </Box>
            </Box>
          </Stack>

          <Formik
            initialValues={{
              status: [true, false],
            }}
            onSubmit={() => console.log('test')}
          >
            {({ values }) => (
              <>
                <ViewFormikContext />
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column-reverse', md: 'row' },
                    gap: { xs: 2, md: 2 },
                    width: '100%',
                  }}
                >
                  <Box
                    className="button-tiles-menu-mcp"
                    sx={{ width: '100%', padding: '0 10px' }}
                  >
                    <AppFormikButtonTiles
                      variant="outlined"
                      name="status"
                      size="small"
                      label=""
                      disabled={false}
                      multichoice={false}
                      className="app-group-button"
                      buttons={[
                        {
                          value: 'Basic Identification',
                          tooltip:
                            'View core metadata including server name, ID, provider details, and primary administrative ownership.',
                        },
                        {
                          value: 'MCP Details',
                          tooltip:
                            'Inspect technical specifications, protocol endpoints, host configuration, and active connection settings.',
                        },
                        {
                          value: 'Capability Profile',
                          tooltip:
                            'Review business tasks, connected enterprise systems, accessed data sources, supported actions, agentic workflows, and side-effect permissions.',
                        },
                        {
                          value: 'Identity & Access',
                          tooltip:
                            'Manage authentication keys, role-based access permissions, and integration credentials for this server.',
                        },
                      ]}
                      outerLabel
                    />

                    <Box
                      sx={{
                        padding: '7px',
                        background: 'white',
                        borderRadius: 2,
                        p: 5,
                      }}
                      mt={3}
                    >
                      {values.status[0] && (
                        <>
                          <Typography sx={{ fontSize: 22, fontWeight: 600 }}>
                            Basic Identification
                          </Typography>
                          <Divider
                            sx={{
                              background: '#d1f27a',
                              borderBottomWidth: 1.2,
                              margin: '1rem 0',
                            }}
                          />
                          <BasicIdentification data={serverDetail} />
                        </>
                      )}
                      {values.status[1] && (
                        <>
                          <Typography sx={{ fontSize: 22, fontWeight: 600 }}>
                            MCP-Specific Details
                          </Typography>
                          <Divider
                            sx={{
                              background: '#d1f27a',
                              borderBottomWidth: 1.2,
                              margin: '1rem 0',
                            }}
                          />
                          <MCPSpecificDetails data={serverDetail} />
                        </>
                      )}
                      {values.status[2] && (
                        <>
                          <Typography sx={{ fontSize: 22, fontWeight: 600 }}>
                            Capability & Function Profile
                          </Typography>
                          <Divider
                            sx={{
                              background: '#d1f27a',
                              borderBottomWidth: 1.2,
                              margin: '1rem 0',
                            }}
                          />
                          <CapabilityProfile data={serverDetail} />
                        </>
                      )}
                      {values.status[3] && (
                        <>
                          <Typography sx={{ fontSize: 22, fontWeight: 600 }}>
                            Identity, Auth & Access Control
                          </Typography>
                          <Divider
                            sx={{
                              background: '#d1f27a',
                              borderBottomWidth: 1.2,
                              margin: '1rem 0',
                            }}
                          />
                          <IdentityAndAccess data={serverDetail} />
                        </>
                      )}
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row', md: 'column' },
                      gap: { xs: 2, sm: 2, md: 0 },
                      alignItems: 'stretch',
                      width: { xs: '100%', sm: '100%', md: 'auto' },
                    }}
                  >
                    <Box
                      sx={{
                        padding: '10px',
                        background: 'white',
                        borderRadius: '10px',
                        minWidth: { xs: 'auto', sm: 0, md: '17.5rem' },
                        height: {
                          xs: 'max-content',
                          sm: 'auto',
                          md: 'max-content',
                        },
                        flex: { xs: 'unset', sm: 1, md: 'unset' },
                        display: { xs: 'block', sm: 'flex', md: 'block' },
                        flexDirection: 'column',
                      }}
                    >
                      <Stack
                        direction="column"
                        spacing={1}
                        sx={{ flex: { sm: 1, md: 'unset' } }}
                      >
                        <Stack spacing={2}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontSize: '24px !important',
                              fontWeight: '700 !important',
                            }}
                          >
                            Approval Status
                          </Typography>
                          <Typography variant="body1" className="text-gray">
                            Governance approval for this MCP server
                          </Typography>
                        </Stack>

                        <Box className="my-5">
                          <Chip
                            size="small"
                            sx={{
                              background: getStatusColors(
                                serverDetail.approval_status
                              )?.background,
                              color: getStatusColors(
                                serverDetail.approval_status
                              )?.color,
                              alignSelf: 'flex-start',
                            }}
                            label={serverDetail.approval_status ?? 'Draft'}
                          />
                        </Box>

                        <Box
                          onClick={handleClick}
                          className="solutions-approval-status cursor"
                          sx={{
                            cursor: 'pointer',
                            mt: { sm: 'auto', md: 0 },
                          }}
                        >
                          {orgACLConfig.aiInventory?.includes(
                            'aiInventory.mcpServers.view.approvalStatus'
                          ) && (
                            <Stack
                              direction="row"
                              spacing={2}
                              sx={{
                                alignItems: 'center',
                                border: '1px solid #f3f4f6',
                                borderRadius: '4px',
                                padding: '8px 10px',
                                justifyContent: 'space-between',
                              }}
                            >
                              <Typography
                                component="span"
                                variant="body2"
                                sx={{
                                  fontSize: '14px !important',
                                }}
                              >
                                {serverDetail.approval_status ??
                                  'Select status'}
                              </Typography>

                              <KeyboardArrowDownIcon />
                            </Stack>
                          )}
                        </Box>

                        <Menu
                          anchorEl={anchorEl}
                          open={open}
                          onClose={handleClose}
                        >
                          {statusMenus.map((status) => (
                            <MenuItem
                              key={status}
                              onClick={() => {
                                handleUpdateApproveStatus(status);
                              }}
                            >
                              {status}
                            </MenuItem>
                          ))}
                        </Menu>
                      </Stack>
                    </Box>

                    {/* -------- SERVER STATUS CARD -------- */}
                    <Box
                      sx={{
                        padding: '10px',
                        borderRadius: '10px',
                        background: '#F1F5F4',
                        minWidth: { xs: 'auto', sm: 0, md: '17.5rem' },
                        height: {
                          xs: 'max-content',
                          sm: 'auto',
                          md: 'max-content',
                        },
                        flex: { xs: 'unset', sm: 1, md: 'unset' },
                        display: { xs: 'block', sm: 'flex', md: 'block' },
                        flexDirection: 'column',
                        mt: { xs: 0, sm: 0, md: 5 },
                      }}
                    >
                      <Stack
                        direction="column"
                        spacing={1}
                        sx={{ flex: { sm: 1, md: 'unset' } }}
                      >
                        <Box
                          className="solutions-status"
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            height: { sm: '100%', md: 'auto' },
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              fontSize: '24px !important',
                              fontWeight: '700 !important',
                            }}
                            mb={4}
                          >
                            Server Status
                          </Typography>
                          <Chip
                            size="small"
                            sx={{
                              background: getStatusColors(serverDetail.status)
                                ?.background,
                              color: getStatusColors(serverDetail.status)
                                ?.color,
                              textTransform: 'capitalize',
                              alignSelf: 'flex-start',
                            }}
                            label={serverDetail.status ?? 'proposed'}
                          />
                        </Box>
                      </Stack>
                    </Box>
                  </Box>
                </Box>
              </>
            )}
          </Formik>
        </Stack>
      )}

      <ConfirmDialog
        title="Delete MCP Server"
        subTitle="Are you sure to delete the server?"
        showConfirm={openDeleteServer}
        cancelTitle="Cancel"
        successTitle="Delete"
        onSuccess={() => {
          handleCloseDeleteAISystem();
          handleDeleteAISystem(mcpId);
        }}
        onCancel={handleCloseDeleteAISystem}
        onCloseDialog={handleCloseDeleteAISystem}
      />
    </Box>
  );
};

export default RecordMCPServer;
